import type {
  Campaign,
  Segment,
  SegmentCounts,
  SegmentLatestRun,
  SegmentRun,
  SegmentStatus,
  SegmentWithProgress,
} from "@/api/campaigns.service";
import { formatDateTime } from "@/utils";

export type CampaignChipColor = "primary" | "default" | "success" | "error" | "warning";

export type CampaignChip = {
  label: string;
  color: CampaignChipColor;
  /**
   * CSS color for the small status dot rendered before the chip label. Derived
   * from the chip color so the list and detail views stay visually consistent
   * with the design mock (Draft = grey, Ready = primary, Sent = green).
   */
  dotColor: string;
};

/**
 * Maps a campaign status onto its display chip label + color. Accepts a plain
 * string so it can render `effective_status` (typed as string by the API) as
 * well as the narrow lifecycle `status`; unknown values fall back to a neutral
 * chip echoing the raw value.
 */
const STOPPED_CHIP: CampaignChip = {
  label: "Stopped",
  color: "warning",
  dotColor: "var(--color-white)",
};

export function campaignStatusChip(status: string): CampaignChip {
  switch (status as Campaign["status"]) {
    case "synchronizing":
      return {
        label: "Synchronizing…",
        color: "warning",
        dotColor: "var(--color-warning, #d97706)",
      };
    case "draft":
      return { label: "Draft", color: "default", dotColor: "var(--color-text-secondary)" };
    case "ready":
      return { label: "Ready", color: "primary", dotColor: "var(--color-primary)" };
    case "scheduled":
      return { label: "Scheduled", color: "primary", dotColor: "var(--color-primary)" };
    case "sending":
      return { label: "Sending", color: "primary", dotColor: "var(--color-primary)" };
    case "sent":
      return { label: "Sent", color: "success", dotColor: "var(--color-white)" };
    case "failed":
      return STOPPED_CHIP;
    default:
      return { label: status, color: "default", dotColor: "var(--color-text-secondary)" };
  }
}

/** Human-readable label for a campaign's type. */
export function campaignTypeLabel(type: Campaign["type"]): string {
  return type === "survey_request" ? "Survey request" : "Reminder";
}

// Effective statuses in which a campaign has actually dispatched emails. Used to
// surface coverage even before `recipients_sent` ticks up (e.g. a `sending`
// campaign whose first batch is still in flight). Draft/scheduled/not-started
// are deliberately excluded so undispatched rows stay uncluttered.
const DISPATCHED_STATUSES = new Set(["sending", "sent", "failed"]);

/**
 * Builds the "sent / total" dispatch-coverage label for a campaign, or null when
 * it hasn't dispatched yet (draft/scheduled/not-started) or has no audience.
 * `sent < total` visibly signals a partial dispatch — e.g. "1 / 18" when only
 * one of 18 candidates was actually sent to — which a bare "Sent" chip hides.
 */
export function campaignCoverageLabel(
  campaign: Pick<Campaign, "recipients_sent" | "recipients_total" | "effective_status">,
): string | null {
  const { recipients_sent, recipients_total, effective_status } = campaign;
  const dispatched = recipients_sent > 0 || DISPATCHED_STATUSES.has(effective_status);
  if (!dispatched || recipients_total <= 0) return null;
  return `${recipients_sent} / ${recipients_total}`;
}

/**
 * Maps a send-segment status onto its display chip. Mirrors the campaign chip
 * palette (Draft = grey, Scheduled/Sending = primary, Sent = green) and adds a
 * red "Canceled" state. Falls back to a neutral chip for any unknown status.
 */
export function segmentStatusChip(status: string): CampaignChip {
  switch (status as SegmentStatus) {
    case "draft":
      return { label: "Draft", color: "default", dotColor: "var(--color-text-secondary)" };
    case "scheduled":
      return { label: "Scheduled", color: "primary", dotColor: "var(--color-primary)" };
    case "sending":
      return { label: "Sending", color: "primary", dotColor: "var(--color-primary)" };
    case "sent":
      return { label: "Sent", color: "success", dotColor: "var(--color-white)" };
    case "canceled":
      return { label: "Canceled", color: "error", dotColor: "var(--color-error)" };
    default:
      return { label: status, color: "default", dotColor: "var(--color-text-secondary)" };
  }
}

/**
 * The chip a segment renders, driven by the segment's own status. A segment
 * still marked `sending` whose newest run failed is the one state nothing owns
 * any more, so it reads "Stopped"; once the segment moves on (a resume puts it
 * back to `scheduled`) the older failed run no longer speaks for it. A `sent`
 * segment whose run had failures reads "Sent · N failed" so a partial failure
 * never looks like a clean success.
 */
export function segmentDisplayChip(
  segment: Pick<Segment, "status" | "run"> & { latest_run?: SegmentLatestRun | null },
): CampaignChip {
  if (segment.status === "sending" && segment.latest_run?.status === "failed") return STOPPED_CHIP;
  const failed = segment.run?.failed ?? 0;
  if (segment.status === "sent" && failed > 0) {
    return {
      label: `Sent · ${failed.toLocaleString()} failed`,
      color: "warning",
      dotColor: "var(--color-warning, #d97706)",
    };
  }
  return segmentStatusChip(segment.status);
}

/**
 * The "emailed / targets" progress a segment shows, taken from the segment's own
 * counts. A run's `total` is the work of that one attempt — a resumed send's run
 * covers only the leftovers — so it can never stand in for the segment's size.
 * Returns null for a segment with no frozen targets, which has no progress yet.
 */
export function segmentProgressLabel(counts: SegmentCounts): string | null {
  if (counts.targets <= 0) return null;
  return `${counts.emailed.toLocaleString()} / ${counts.targets.toLocaleString()}`;
}

/**
 * How many targets a COMPLETED run neither submitted, failed, nor suppressed —
 * i.e. rows silently skipped by the per-campaign dedup because another send in
 * this campaign already claimed the email. While the run is still going the
 * remainder is just "not processed yet", so this returns 0 until completion.
 */
export function segmentRunSkipped(run: SegmentRun): number {
  if (run.status !== "completed") return 0;
  return Math.max(0, run.total - run.submitted - run.failed - run.suppressed);
}

/**
 * Tooltip for the "N skipped" badge. Explains the per-campaign dedup and, since
 * the run exposes its `claimed` counter, states how many rows this run actually
 * claimed so the operator can reconcile the arithmetic.
 */
export function segmentSkippedHint(run: SegmentRun): string {
  return (
    "Skipped — already sent earlier in this campaign. Each recipient receives at most " +
    `one email per campaign. Claimed by this run: ${run.claimed.toLocaleString()} of ${run.total.toLocaleString()}.`
  );
}

/**
 * Produces a coarse "in ~N units" hint for a future instant using
 * Intl.RelativeTimeFormat. Returns undefined for a past/now instant (nothing to
 * count down to) — the caller only shows the hint when it is present.
 */
function relativeHint(target: Date): string | undefined {
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return undefined;

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) return `in ~${rtf.format(minutes, "minute").replace(/^in /, "")}`;
  const hours = Math.round(diffMs / 3_600_000);
  if (hours < 48) return `in ~${rtf.format(hours, "hour").replace(/^in /, "")}`;
  const days = Math.round(diffMs / 86_400_000);
  return `in ~${rtf.format(days, "day").replace(/^in /, "")}`;
}

export type SegmentScheduleDisplay = {
  text: string;
  variant: "muted" | "immediate" | "normal";
  relative?: string;
};

/**
 * Renders the truth of a segment's "Scheduled at" cell per lifecycle state:
 * drafts read "Not scheduled"; a send-now (scheduled with no absolute time) or
 * an in-flight send reads "Sending now"; a future schedule shows its local
 * timestamp plus a coarse relative hint; a sent send shows when it went out.
 */
export function segmentScheduleDisplay(
  segment: Segment & { latest_run?: SegmentLatestRun | null },
): SegmentScheduleDisplay {
  if (segment.status === "sending" && segment.latest_run?.status === "failed") {
    const stoppedAt = segment.latest_run.finished_at ?? segment.latest_run.started_at;
    return { text: formatDateTime(stoppedAt), variant: "normal" };
  }

  switch (segment.status as SegmentStatus) {
    case "draft":
      return { text: "Not scheduled", variant: "muted" };
    case "scheduled": {
      if (!segment.scheduled_at) return { text: "Sending now", variant: "immediate" };
      const when = new Date(segment.scheduled_at);
      return { text: formatDateTime(when), variant: "normal", relative: relativeHint(when) };
    }
    case "sending":
      return { text: "Sending now", variant: "immediate" };
    case "sent": {
      const iso = segment.sent_at ?? segment.scheduled_at;
      if (!iso) return { text: "—", variant: "muted" };
      return { text: formatDateTime(iso), variant: "normal" };
    }
    default:
      return { text: "—", variant: "muted" };
  }
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

/** The one-sentence summary a send leads with, built from its counts. */
export function segmentEmailedHeadline(counts: SegmentCounts): string {
  if (counts.targets === 0) return "This send has no recipients.";
  const emailed = counts.emailed.toLocaleString();
  const targets = counts.targets.toLocaleString();
  const tail = plural(counts.targets, "person has been emailed", "people have been emailed");
  return `${emailed} of ${targets} ${tail}.`;
}

export type SegmentExceptionKind = "not_emailed_yet" | "unconfirmed" | "skipped";

export type SegmentExceptionLine = {
  kind: SegmentExceptionKind;
  text: string;
  note?: string;
};

const UNCONFIRMED_NOTE =
  "We cannot tell whether these went out, so nobody will be emailed a second time by mistake. " +
  "Copy the details and the development team will confirm what happened to them.";

/** The exceptions in a send's counts that a human still has to deal with. */
export function segmentExceptionLines(counts: SegmentCounts): SegmentExceptionLine[] {
  const lines: SegmentExceptionLine[] = [];

  if (counts.not_emailed_yet > 0) {
    lines.push({
      kind: "not_emailed_yet",
      text: `${counts.not_emailed_yet.toLocaleString()} ${plural(
        counts.not_emailed_yet,
        "person has",
        "people have",
      )} not been emailed yet`,
    });
  }

  if (counts.unconfirmed > 0) {
    lines.push({
      kind: "unconfirmed",
      text: `${counts.unconfirmed.toLocaleString()} ${plural(
        counts.unconfirmed,
        "message",
        "messages",
      )} we cannot confirm`,
      note: UNCONFIRMED_NOTE,
    });
  }

  if (counts.skipped > 0) {
    lines.push({
      kind: "skipped",
      text: `${counts.skipped.toLocaleString()} ${plural(
        counts.skipped,
        "person was",
        "people were",
      )} skipped on purpose`,
    });
  }

  return lines;
}

/** Confirmation shown after a resume, quoting how many recipients went back in the queue. */
export function segmentResumedMessage(resumedRecipients: number): string {
  if (resumedRecipients === 0) return "Nothing left to send — no one was put back in the queue.";
  return `Continuing — ${resumedRecipients.toLocaleString()} ${plural(
    resumedRecipients,
    "person is",
    "people are",
  )} back in the queue.`;
}

/** Turns a failed resume call into a sentence, never a raw error code. */
export function segmentResumeErrorMessage(err: unknown): string {
  const { code, status, message } = (err ?? {}) as {
    code?: string;
    status?: number;
    message?: string;
  };

  if (code === "RUN_IN_PROGRESS") {
    return "This send is already running again — give it a moment, then reload.";
  }
  if (code === "SEGMENT_NOT_RESUMABLE") {
    return "This send can't be continued — no one is left waiting in its queue.";
  }
  if (status === 404) return "This send no longer exists.";
  return message || "Failed to continue this send.";
}

/** Clipboard text naming the send, its latest run and the error behind it. */
export function segmentCopyDetails(
  campaignName: string,
  segment: Pick<SegmentWithProgress, "id" | "label" | "latest_run">,
): string {
  const label = segment.label ?? "unnamed";
  return [
    `Campaign: ${campaignName}`,
    `Send: ${label} (${segment.id})`,
    `Latest run: ${segment.latest_run?.id ?? "none recorded"}`,
    `Error: ${segment.latest_run?.error ?? "none recorded"}`,
  ].join("\n");
}

/** Timestamp range for a run in the send history. */
export function segmentRunPeriod(run: { started_at: string; finished_at: string | null }): string {
  const started = formatDateTime(run.started_at);
  if (!run.finished_at) return `${started} — still running`;
  return `${started} — ${formatDateTime(run.finished_at)}`;
}

/** Display label for a run's own status in the send history. */
export function segmentRunStatusLabel(status: string): string {
  switch (status) {
    case "running":
      return "Running";
    case "completed":
      return "Completed";
    case "failed":
      return "Stopped";
    default:
      return status;
  }
}
